import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthUIStore } from '../store/authUIStore';
import { getSupabase } from '../lib/supabase';
import { pathPreservation } from '../lib/utils';
import { logger } from '../utils/logger';
import { authKeys } from './queries/authKeys';
import { User } from '../types/supabase';
import { permissionKeys } from './queries/permissionKeys';

/**
 * Hook that initializes authentication and sets up auth state change listener
 * This hook uses React Query for auth state management
 */
export function useAuthInitialization() {
  const { isInitialized, setInitialized } = useAuthUIStore();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);

  // Track if initialization has been done
  const didInitAuth = useRef(false);

  // Set a flag when the page is about to be refreshed
  // This helps us distinguish between refresh and normal navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem('isRefreshing', 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Single auth initialization effect
  useEffect(() => {
    // Skip if we've already initialized auth to prevent infinite loops
    if (didInitAuth.current) {
      // Set loading to false if we're already initialized
      if (loading) {
        setLoading(false);
      }
      return;
    }
    
    let mounted = true;
    let authSubscription = null;

    const initializeAuth = async () => {
      logger.auth('Starting auth initialization');

      // Mark as initialized to prevent multiple runs
      didInitAuth.current = true;

      try {
        // Get Supabase client
        const client = await getSupabase();

        // Get current session
        const {
          data: { session },
        } = await client.auth.getSession();

        if (!mounted) return;

        // Set user from session
        if (session?.user) {
          logger.auth('User session found', { userId: session.user.id });

          // Set user in React Query cache
          queryClient.setQueryData(authKeys.user(), session.user);

          // No need to fetch permissions directly - React Query will handle this
          // Once the user is set in the cache, components using usePermissions()
          // will automatically fetch permissions when they mount

          // Check for path restoration
          const currentPath = window.location.pathname;
          if (currentPath === '/' && pathPreservation.shouldRestorePath(currentPath)) {
            logger.auth('Auth initialized with a saved path to restore');
          }
        } else {
          logger.auth('No user session found');

          // Set user to null in React Query cache
          queryClient.setQueryData(authKeys.user(), null);
        }

        // Set up auth state change listener
        const {
          data: { subscription },
        } = client.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) return;

          logger.auth('Auth state changed', { event });

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (session?.user) {
              // Update user in React Query cache
              queryClient.setQueryData(authKeys.user(), session.user);

              // Always keep the initialized flag true after first initialization
              setInitialized(true);

              // Only invalidate permission queries on SIGNED_IN, not TOKEN_REFRESHED
              if (event === 'SIGNED_IN') {
                // Check if the auth has already been initialized
                // If it has, then we're likely in a tab visibility change
                const isAlreadyInitialized = isInitialized;
                const isVisibilityChange =
                  document.visibilityState === 'visible' && isAlreadyInitialized;

                logger.auth('🔄 Auth handling SIGNED_IN event', {
                  isAlreadyInitialized,
                  isVisibilityChange,
                  willInvalidatePermissions: true,
                  willSetLoadingTrue: !isVisibilityChange,
                  timestamp: new Date().toISOString(),
                });

                // Only set loading to true if this isn't a visibility change
                if (!isVisibilityChange) {
                  // Invalidate permissions query to trigger a refetch
                  logger.auth('Invalidating permissions on sign-in');
                  queryClient.invalidateQueries({
                    queryKey: permissionKeys.user(session.user.id),
                  });

                  // Check for path restoration
                  const currentPath = window.location.pathname;
                  if (currentPath === '/' && pathPreservation.shouldRestorePath(currentPath)) {
                    logger.auth('User signed in with a saved path to restore');
                  }
                } else {
                  logger.auth('Skipping loading state for tab visibility SIGNED_IN event');
                  // Still invalidate permissions in background without setting loading state
                  queryClient.invalidateQueries({
                    queryKey: permissionKeys.user(session.user.id),
                  });
                }
              }
            }
          } else if (event === 'SIGNED_OUT') {
            // Set user to null in React Query cache on sign out
            queryClient.setQueryData(authKeys.user(), null);

            // Clear saved path on sign out
            pathPreservation.clearSavedPath();
          }
        });

        authSubscription = subscription;
        logger.auth('Auth initialization completed successfully');

        // Mark auth as initialized when complete
        if (mounted) {
          setInitialized(true);
          // Update loading state
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        logger.error('Auth initialization error', error);

        // Even on error, we need to stop loading state to prevent blocking UI
        queryClient.setQueryData(authKeys.user(), null);

        if (mounted) {
          // Update loading state
          setLoading(false);
        }
      }
    };

    // Start auth initialization
    initializeAuth();

    // Cleanup on unmount
    return () => {
      mounted = false;

      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [isInitialized, loading, queryClient, setInitialized]);

  // Get user from React Query cache
  const user = queryClient.getQueryData<User | null>(authKeys.user());

  // Return needed values
  return { isAuthenticated: !!user, isInitialized, loading };
}

export default useAuthInitialization;
